import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Typography
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetOne, useNotify, useTranslate } from 'react-admin';
import { getPumpSpeedXls } from '../../../../core/services/pump-api.service';
import { downloadFile } from '../../../../core/utils/file-utils';
import { EXPORT_ZIP_DATA } from '../../../../core/constants/file.constants';

interface PumpSpeed {
  id: string;
  speed: number;
  pumpLines: unknown[];
}

interface Props {
  pumpId: string;
  open: boolean;
  onClose: () => void;
}

export const PumpsSpeedExportDialog = ({ pumpId, open, onClose }: Props) => {
  const t = useTranslate();
  const notify = useNotify();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  const { data: pumpData, isLoading } = useGetOne(
    'pumps',
    { id: pumpId },
    { enabled: open && !!pumpId }
  );

  const speeds = useMemo(
    () =>
      ((pumpData?.pumpSpeeds ?? []) as PumpSpeed[])
        .filter((s) => s.pumpLines && s.pumpLines.length > 0)
        .sort((a, b) => a.speed - b.speed),
    [pumpData]
  );

  useEffect(() => {
    if (open) {
      setSelectedIds(speeds.map((s) => s.id));
    }
  }, [open, speeds]);

  const allSelected = speeds.length > 0 && selectedIds.length === speeds.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = () =>
    setSelectedIds(allSelected ? [] : speeds.map((s) => s.id));

  const toggleOne = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const handleClose = () => {
    if (!exporting) onClose();
  };

  const handleExport = async () => {
    if (selectedIds.length === 0) return;
    setExporting(true);
    try {
      const response = await getPumpSpeedXls(pumpId, selectedIds);
      const base64Response = await fetch(
        `${EXPORT_ZIP_DATA},${response.data.file}`
      );
      const blob = await base64Response.blob();
      downloadFile(blob, response.data.fileName);
      notify('resources.pumps.speeds.export.success', { type: 'success' });
      onClose();
    } catch {
      notify('ra.notification.http_error', { type: 'warning' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('resources.pumps.speeds.export.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t('resources.pumps.speeds.export.description')}
        </DialogContentText>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : speeds.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            {t('resources.pumps.speeds.export.noData')}
          </Typography>
        ) : (
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                  disabled={exporting}
                />
              }
              label={t('resources.pumps.speeds.export.selectAll')}
            />
            <Divider sx={{ my: 1 }} />
            <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
              {speeds.map((s) => (
                <FormControlLabel
                  key={s.id}
                  sx={{ display: 'flex' }}
                  control={
                    <Checkbox
                      checked={selectedIds.includes(s.id)}
                      onChange={() => toggleOne(s.id)}
                      disabled={exporting}
                    />
                  }
                  label={`${s.speed} RPM`}
                />
              ))}
            </Box>
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={exporting}>
          {t('shared.form.toolbar.actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={exporting || isLoading || selectedIds.length === 0}
          startIcon={
            exporting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t('resources.pumps.speeds.export.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
